import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, ChevronUp, ChevronDown, MoreHorizontal, RefreshCw, AlertCircle, Plus, Edit, Save, X, Edit3 } from 'lucide-react';
import { stcBinGrpRelationService, StcBinGrpRelationDto, CreateStcBinGrpRelationRequest } from '../../../../../services/stcBinGrpRelationService';
import { stcBinGrpService, StcBinGrpDto } from '../../../../../services/stcBinGrpService';
import { useAuth } from '../../../../../contexts/AuthContext';

interface ColumnFilter {
    [key: string]: string;
}

interface SortConfig {
    key: string;
    direction: 'asc' | 'desc';
}

interface SelectedRows {
    [key: string]: boolean;
}

interface BinGrpRelationDataTableProps {
    selectedRows: SelectedRows;
    onRowSelection: (rowId: string, selected: boolean) => void;
    onBulkSelection: (rows: SelectedRows) => void;
    onDataChange: (data: StcBinGrpRelationDto[]) => void;
    onOpenSidePanel: () => void;
    refreshTrigger?: number;
}

export const BinGrpRelationDataTable: React.FC<BinGrpRelationDataTableProps> = ({
    selectedRows,
    onRowSelection,
    onBulkSelection,
    onDataChange,
    onOpenSidePanel,
    refreshTrigger
}) => {
    const [columnFilters, setColumnFilters] = useState<ColumnFilter>({});
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [data, setData] = useState<StcBinGrpRelationDto[]>([]);
    const [binGroups, setBinGroups] = useState<StcBinGrpDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
    
    const { user } = useAuth();

    const fields = ['masterBinGrpNo', 'childBinGrpNo'];
    
    useEffect(() => {
        loadData();
    }, []);

    // React to refresh trigger from parent component
    useEffect(() => {
        if (refreshTrigger && refreshTrigger > 0) {
            loadData();
        }
    }, [refreshTrigger]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError('');
            
            let relations: StcBinGrpRelationDto[] = [];
            let groups: StcBinGrpDto[] = [];
            
            try {
                relations = await stcBinGrpRelationService.getCurrent();
                groups = await stcBinGrpService.getCurrent();
            } catch (err) {
                try {
                    relations = await stcBinGrpRelationService.getAll();
                    groups = await stcBinGrpService.getAll();
                } catch (err2) {
                    relations = [];
                    groups = [];
                }
            }
            
            setData(relations);
            setBinGroups(groups);
            onDataChange(relations);
        } catch (err: any) {
            setError(err.message || 'Feil ved lasting av data');
        } finally {
            setLoading(false);
        }
    };

    const getBinGrpDescription = (binGrpNo: number): string => {
        const group = binGroups.find(g => g.binGrpNo === binGrpNo);
        return group?.description1 || '';
    };

    const CreateForm: React.FC<{ onClose: () => void; onSave: (request: CreateStcBinGrpRelationRequest) => void }> = ({ onClose, onSave }) => {
        const [formData, setFormData] = useState<CreateStcBinGrpRelationRequest>({
            companyNo: user?.companyNo || '001',
            masterBinGrpNo: 0,
            childBinGrpNo: 0
        });

        const handleSubmit = (e: React.FormEvent) => {
            e.preventDefault();
            if (formData.masterBinGrpNo === formData.childBinGrpNo) {
                alert('Master og barn kan ikke være samme gruppe');
                return;
            }
            onSave(formData);
        };

        return (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Opprett ny lokasjonsgrupperelasjon</h4>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Selskap</label>
                            <input
                                type="text"
                                value={formData.companyNo}
                                onChange={(e) => setFormData(prev => ({ ...prev, companyNo: e.target.value }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            />
                        </div>
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Master-gruppe</label>
                            <select
                                value={formData.masterBinGrpNo}
                                onChange={(e) => setFormData(prev => ({ ...prev, masterBinGrpNo: parseInt(e.target.value) || 0 }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            >
                                <option value={0}>Velg master-gruppe...</option>
                                {binGroups.map(group => (
                                    <option key={group.id} value={group.binGrpNo}>
                                        {group.binGrpNo} - {group.description1}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Barn-gruppe</label>
                            <select
                                value={formData.childBinGrpNo}
                                onChange={(e) => setFormData(prev => ({ ...prev, childBinGrpNo: parseInt(e.target.value) || 0 }))}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                required
                            >
                                <option value={0}>Velg barn-gruppe...</option>
                                {binGroups.map(group => (
                                    <option key={group.id} value={group.binGrpNo}>
                                        {group.binGrpNo} - {group.description1}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="submit"
                            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Opprett
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                        >
                            Avbryt
                        </button>
                    </div>
                </form>
            </div>
        );
    };

    const handleCreate = async (request: CreateStcBinGrpRelationRequest) => {
        try {
            setLoading(true);
            await stcBinGrpRelationService.create(request);
            setShowCreateForm(false);
            await loadData();
        } catch (err: any) {
            setError(err.message || 'Feil ved opprettelse');
        } finally {
            setLoading(false);
        }
    };

    // Filter data based on column filters and global search
    const filteredData = useMemo(() => {
        let filtered = data;

        // Apply global filter
        if (globalFilter) {
            filtered = filtered.filter(row =>
                Object.values(row).some(value => 
                    value?.toString().toLowerCase().includes(globalFilter.toLowerCase())
                )
            );
        }

        // Apply column filters
        Object.entries(columnFilters).forEach(([column, filterValue]) => {
            if (filterValue) {
                filtered = filtered.filter(row => {
                    const fieldValue = (row as any)[column];
                    return fieldValue?.toString().toLowerCase().includes(filterValue.toLowerCase());
                });
            }
        });

        return filtered;
    }, [data, columnFilters, globalFilter]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            const aValue = (a as any)[sortConfig.key];
            const bValue = (b as any)[sortConfig.key];

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const handleSort = (key: string) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleColumnFilter = (column: string, value: string) => {
        setColumnFilters(prev => ({
            ...prev,
            [column]: value
        }));
    };

    const handleSelectAll = (checked: boolean) => {
        const newSelection: SelectedRows = {};
        if (checked) {
            sortedData.forEach(row => {
                newSelection[row.id.toString()] = true;
            });
        }
        onBulkSelection(newSelection);
    };

    const isAllSelected = sortedData.length > 0 && sortedData.every(row => selectedRows[row.id.toString()]);
    const isIndeterminate = sortedData.some(row => selectedRows[row.id.toString()]) && !isAllSelected;

    const getFieldLabel = (field: string): string => {
        const labels: { [key: string]: string } = {
            'masterBinGrpNo': 'Master-gruppe',
            'childBinGrpNo': 'Barn-gruppe'
        };
        return labels[field] || field;
    };

    if (loading && data.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-3" />
                    <span className="text-gray-600">Laster lokasjonsgrupperelasjon...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="flex items-center justify-center text-red-600">
                    <AlertCircle className="w-6 h-6 mr-3" />
                    <div>
                        <p className="font-medium">Feil ved lasting av data</p>
                        <p className="text-sm text-gray-600">{error}</p>
                        <button
                            onClick={loadData}
                            className="mt-2 px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Prøv igjen
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Table Header Controls */}
            <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-medium text-gray-900">Lokasjonsgrupperelasjon</h3>
                        <span className="text-sm text-gray-500">
                            ({sortedData.length} av {data.length} rader)
                        </span>
                        {loading && (
                            <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                        )}
                    </div>
                    
                    <div className="flex items-center gap-3">
                        {/* Global Search */}
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Søk i alle kolonner..."
                                value={globalFilter}
                                onChange={(e) => setGlobalFilter(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        
                        <button
                            onClick={() => setShowCreateForm(!showCreateForm)}
                            className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Ny
                        </button>
                        
                        <button
                            onClick={onOpenSidePanel}
                            disabled={Object.values(selectedRows).filter(Boolean).length === 0}
                            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                                Object.values(selectedRows).filter(Boolean).length > 0
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            <Edit3 className="w-4 h-4" />
                            Rediger ({Object.values(selectedRows).filter(Boolean).length})
                        </button>
                        
                        <button 
                            onClick={loadData}
                            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            <span className="text-sm">Oppdater</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Create Form */}
            {showCreateForm && (
                <CreateForm 
                    onClose={() => setShowCreateForm(false)}
                    onSave={handleCreate}
                />
            )}

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            {/* Select All Checkbox */}
                            <th className="w-12 px-4 py-3">
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    ref={(el) => {
                                        if (el) el.indeterminate = isIndeterminate;
                                    }}
                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                />
                            </th>
                            
                            {/* Column Headers */}
                            {fields.map(field => (
                                <th key={field} className="px-4 py-3 text-left">
                                    <div className="space-y-2">
                                        {/* Sort Header */}
                                        <button
                                            onClick={() => handleSort(field)}
                                            className="flex items-center gap-1 text-xs font-medium text-gray-700 hover:text-gray-900 transition-colors"
                                        >
                                            <span>{getFieldLabel(field)}</span>
                                            {sortConfig.key === field ? (
                                                sortConfig.direction === 'asc' ? (
                                                    <ChevronUp className="w-3 h-3" />
                                                ) : (
                                                    <ChevronDown className="w-3 h-3" />
                                                )
                                            ) : (
                                                <div className="w-3 h-3" />
                                            )}
                                        </button>
                                        
                                        {/* Column Filter */}
                                        <input
                                            type="text"
                                            placeholder={`Filter ${getFieldLabel(field)}...`}
                                            value={columnFilters[field] || ''}
                                            onChange={(e) => handleColumnFilter(field, e.target.value)}
                                            className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </th>
                            ))}
                            
                            {/* Description Columns */}
                            <th className="px-4 py-3 text-left">
                                <span className="text-xs font-medium text-gray-700">Master beskrivelse</span>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <span className="text-xs font-medium text-gray-700">Barn beskrivelse</span>
                            </th>
                        </tr>
                    </thead>
                    
                    <tbody className="divide-y divide-gray-200">
                        {sortedData.map(row => {
                            return (
                                <tr 
                                    key={row.id} 
                                    className={`hover:bg-gray-50 transition-colors ${
                                        selectedRows[row.id.toString()] ? 'bg-blue-50' : ''
                                    }`}
                                >
                                    {/* Row Checkbox */}
                                    <td className="px-4 py-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedRows[row.id.toString()] || false}
                                            onChange={(e) => onRowSelection(row.id.toString(), e.target.checked)}
                                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                    </td>
                                    
                                    {/* Data Columns */}
                                    {fields.map(field => (
                                        <td key={field} className="px-4 py-3">
                                            <span className="text-sm text-gray-900">
                                                {(row as any)[field] || ''}
                                            </span>
                                        </td>
                                    ))}
                                    
                                    {/* Description Columns */}
                                    <td className="px-4 py-3">
                                        <span className="text-sm text-gray-600">
                                            {getBinGrpDescription(row.masterBinGrpNo)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-sm text-gray-600">
                                            {getBinGrpDescription(row.childBinGrpNo)}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Table Footer */}
            {sortedData.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    <p>Ingen data funnet med gjeldende filtre</p>
                </div>
            ) : (
                <div className="p-4 border-t border-gray-200 text-sm text-gray-600">
                    Viser {sortedData.length} av {data.length} rader
                    {Object.values(selectedRows).filter(Boolean).length > 0 && (
                        <span className="ml-4">
                            • {Object.values(selectedRows).filter(Boolean).length} rader valgt
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};
